// ABOUTME: Approval cards in an agent's chat: the actions that chat's own agent asked the owner
// to approve ('marketing', or a team member's id), open and decided, read from
// GET /agent/approvals?chat= and refreshed by the event stream on every approval notification.
// Approving (or Always approve, on a paid job's card) or declining calls the API and refreshes
// every approvals view; a failure is thrown so the card shows it.
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { approveAction, getApprovals, rejectAction, retryAction } from '@/api/agent'
import { useUiStore } from '@/stores/useUiStore'
import type { AgentApprovalResponse } from '@/types'
import type { ApprovalCardActions } from '@/components/approvals/ApprovalCard'

export const APPROVALS_KEY = 'agent-approvals'
const NONE: AgentApprovalResponse[] = []

export function useChatApprovals(chat: string): AgentApprovalResponse[] {
  const { data } = useQuery({
    queryKey: [APPROVALS_KEY, 'chat', chat],
    queryFn: () => getApprovals(20, 0, chat),
    select: (res) => res.approvals,
  })
  return data ?? NONE
}

export function useApprovalActions() {
  const queryClient = useQueryClient()
  const refresh = () => queryClient.invalidateQueries({ queryKey: [APPROVALS_KEY] })
  return {
    approve: async (id: string, always = false) => {
      await approveAction(id, always)
      await refresh()
    },
    reject: async (id: string, reason: string) => {
      await rejectAction(id, reason)
      await refresh()
    },
    retry: async (id: string) => {
      await retryAction(id)
      await refresh()
    },
  }
}

/** The card actions for one approval in a chat; a failure shows as a toast. */
export function useChatCardActions(): (id: string) => ApprovalCardActions {
  const { approve, reject, retry } = useApprovalActions()
  const showToast = useUiStore((s) => s.showToast)
  const guarded = (action: () => Promise<void>) => async () => {
    try {
      await action()
    } catch (err) {
      showToast({ type: 'error', title: (err as Error).message })
    }
  }
  return (id) => ({
    onApprove: guarded(() => approve(id)),
    onAlwaysApprove: guarded(() => approve(id, true)),
    onDecline: (reason) => guarded(() => reject(id, reason))(),
    onRetry: guarded(() => retry(id)),
  })
}
